/**
 * "View Details" dialog (Broker Integration — Phase 1) — a read-only,
 * expanded view of one card's data. Never shows `access_token`/
 * `refresh_token`: those two fields don't exist on `BrokerAccountRead` at
 * all (see `app.schemas.broker_account` — the backend never serializes
 * them), so there is nothing here to accidentally leak.
 */
import type { ReactNode } from "react";
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import type { BrokerAccountRead, BrokerName } from "@/types";
import { API_HEALTH_COLOR, BROKER_META, CONNECTION_STATUS_META, formatDateTime } from "./colors";

interface DetailsDialogProps {
  open: boolean;
  broker: BrokerName | null;
  account: BrokerAccountRead | null;
  onClose: () => void;
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Box display="flex" justifyContent="space-between" alignItems="center" py={0.5}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {value}
      </Typography>
    </Box>
  );
}

export function DetailsDialog({ open, broker, account, onClose }: DetailsDialogProps) {
  if (!broker || !account) return null;
  const meta = BROKER_META[broker];
  const statusMeta = CONNECTION_STATUS_META[account.connection_status];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{meta.displayName} — Account Details</DialogTitle>
      <DialogContent>
        <Stack direction="row" spacing={1} mb={1.5}>
          <Chip size="small" color={statusMeta.color} label={`${statusMeta.dot} ${statusMeta.label}`} />
          {account.is_default && <Chip size="small" color="warning" label="Default Broker" />}
          <Chip size="small" variant="outlined" color={API_HEALTH_COLOR[account.api_health]} label={`API: ${account.api_health}`} />
        </Stack>
        <Divider sx={{ mb: 1 }} />
        <Row label="Connected User ID" value={account.broker_user_id || "—"} />
        <Row label="Client ID" value={account.client_id || "—"} />
        <Row label="Account Name" value={account.account_name || "—"} />
        <Row label="Account Type" value={account.account_type || "—"} />
        <Row label="Last Login Time" value={formatDateTime(account.last_login)} />
        <Row label="Session Expiry" value={formatDateTime(account.session_expiry)} />
        <Row label="Last Health Check" value={formatDateTime(account.last_health_check_at)} />
        <Divider sx={{ my: 1 }} />
        <Row label="Connected Since (first linked)" value={formatDateTime(account.created_at)} />
        <Row label="Last Updated" value={formatDateTime(account.updated_at)} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default DetailsDialog;
